import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useKeyboard } from '../../hooks/useKeyboard';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  hasUnsavedChanges?: boolean;
  size?: 'tiny' | 'xs' | 'small' | 'medium' | 'large';
  variant?: 'info' | 'success' | 'warning' | 'danger';
  footer?: React.ReactNode;
  showCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  hasUnsavedChanges = false,
  size = 'medium',
  variant = 'info',
  footer,
  showCloseButton = true
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    // Only set flag if mousedown is on the overlay itself, not its children
    if (e.target === e.currentTarget) {
      setMouseDownOnOverlay(true);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only close if both mousedown and mouseup happened on the overlay
    if (e.target === e.currentTarget && mouseDownOnOverlay) {
      handleClose();
    }
    setMouseDownOnOverlay(false);
  };

  const confirmClose = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  const cancelClose = () => {
    setShowConfirmDialog(false);
  };

  // Global keyboard handling for main modal
  useKeyboard({
    onEscape: handleClose,
    enabled: isOpen && !showConfirmDialog
  });

  // Global keyboard handling for confirmation dialog
  useKeyboard({
    onEscape: cancelClose,
    onEnter: confirmClose,
    enabled: showConfirmDialog
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    tiny: 'modal-tiny',
    xs: 'max-w-sm',
    small: 'max-w-md',
    medium: 'max-w-2xl',
    large: 'max-w-4xl'
  };

  const variantStyles = {
    info: {
      borderColor: '#3b82f6',
      headerBg: 'rgba(59, 130, 246, 0.05)',
      iconColor: '#3b82f6'
    },
    success: {
      borderColor: '#10b981',
      headerBg: 'rgba(16, 185, 129, 0.05)',
      iconColor: '#10b981'
    },
    warning: {
      borderColor: '#f59e0b',
      headerBg: 'rgba(245, 158, 11, 0.05)',
      iconColor: '#f59e0b'
    },
    danger: {
      borderColor: '#dc2626',
      headerBg: 'rgba(220, 38, 38, 0.05)',
      iconColor: '#dc2626'
    }
  };

  const modalContent = (
    <>
      {/* Main Modal */}
      <div 
        className="modal-overlay" 
        onMouseDown={handleOverlayMouseDown}
        onClick={handleOverlayClick}
      >
        <div
          className={`modal-content ${sizeClasses[size]}`}
          onClick={(e) => e.stopPropagation()}
          style={{ borderColor: variantStyles[variant].borderColor }}
        >
          <div className="modal-header" style={{
            background: variantStyles[variant].headerBg,
            borderBottom: `1px solid ${variantStyles[variant].borderColor}`
          }}>
            <h2 className="modal-title">{title}</h2>
            {showCloseButton && (
              <button
                className="modal-close-btn"
                onClick={handleClose}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            )}
          </div>
          <div className="modal-body">
            {children}
          </div>
          {footer && (
            <div className="modal-footer" style={{
              borderTop: `1px solid ${variantStyles[variant].borderColor}`,
              background: variantStyles[variant].headerBg
            }}>
              {footer}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div 
          className="modal-overlay" 
          style={{ zIndex: 1001 }}
          onMouseDown={(e) => e.target === e.currentTarget && setMouseDownOnOverlay(true)}
          onClick={(e) => {
            if (e.target === e.currentTarget && mouseDownOnOverlay) {
              cancelClose();
            }
            setMouseDownOnOverlay(false);
          }}
        >
          <div className="modal-content max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Unsaved Changes</h3>
            </div>
            <div className="modal-body">
              <p className="confirmation-text">
                You have unsaved changes. Are you sure you want to close without saving?
              </p>
              <div className="confirmation-buttons">
                <button className="btn btn-secondary" onClick={cancelClose}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={confirmClose}>
                  Close Without Saving
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--bg-overlay);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          box-shadow: var(--shadow-heavy);
          max-height: 90vh;
          overflow: hidden;
          border: 2px solid;
          display: flex;
          flex-direction: column;
        }

        .modal-tiny {
          width: 320px !important;
          max-width: 320px !important;
        }

        .max-w-sm {
          width: 400px !important;
          max-width: 400px !important;
        }

        .max-w-md {
          width: 500px !important;
          max-width: 500px !important;
        }

        .max-w-2xl {
          width: 672px !important;
          max-width: 672px !important;
        }

        .max-w-4xl {
          width: 896px !important;
          max-width: 896px !important;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          flex-shrink: 0;
          border-top-left-radius: var(--radius-large);
          border-top-right-radius: var(--radius-large);
        }

        .modal-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
          letter-spacing: -0.01em;
        }

        .modal-close-btn {
          background: var(--bg-secondary);
          border: none;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: var(--border-primary);
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
          min-height: 0;
        }

        .modal-footer {
          padding: 20px 24px;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          align-items: center;
          flex-shrink: 0;
          border-bottom-left-radius: var(--radius-large);
          border-bottom-right-radius: var(--radius-large);
        }

        .modal-footer .btn {
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 500;
          border-radius: var(--radius-medium);
          min-height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .modal-footer .btn-secondary {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          border: 1px solid var(--border-primary);
        }

        .modal-footer .btn-primary {
          background: var(--accent-green);
          color: white;
          border: 1px solid var(--accent-green);
        }

        .modal-footer .btn-danger {
          background: var(--accent-red);
          color: white;
          border: 1px solid var(--accent-red);
        }

        .modal-footer .btn-success {
          background: var(--accent-green);
          color: white;
          border: 1px solid var(--accent-green);
          gap: 8px;
          padding: 12px 24px;
          font-size: 14px;
          min-height: 44px;
        }

        .modal-footer .btn-danger-filled {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
          gap: 8px;
          padding: 12px 24px;
          font-size: 14px;
          min-height: 44px;
          border-radius: var(--radius-medium);
        }

        .modal-footer .btn-danger-filled:hover {
          background: rgba(239, 68, 68, 0.25);
        }

        .modal-footer .btn-warning {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
          border: 1px solid rgba(245, 158, 11, 0.3);
          gap: 8px;
          padding: 12px 24px;
          font-size: 14px;
          min-height: 44px;
        }

        .confirmation-text {
          color: var(--text-secondary);
          margin-bottom: 24px;
          line-height: 1.5;
        }

        .confirmation-buttons {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
      `}</style>
    </>
  );

  // Render modal in a portal at document.body to avoid positioning issues
  return createPortal(modalContent, document.body);
};